import { useEffect, useState } from 'react'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ConditionalAddressControl } from './ConditionalAddressControl'

import { ContainerGroupField } from '@/components/common/ContainerGroupField'
import { AutocompleteControl } from '@/components/form-controls'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { DemographicFormData, OptionsField } from '@/types/forms/'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'

interface HousingControlProps {
    control: Control<DemographicFormData>
    watchCountryResidence?: string
    watchDepartmentResidence?: string
}

const mapApiDataToOptions = (data: any[]): OptionsField[] => {
    return data
        .filter((item: any) => {
            const hasValue =
                item && (item.code || item.codigo || item.id || item.value)
            const hasLabel =
                item &&
                (item.name ||
                    item.nombre ||
                    item.description ||
                    item.descripcion ||
                    item.label)

            return hasValue && hasLabel
        })
        .map((item: any) => ({
            label:
                item.name ||
                item.nombre ||
                item.description ||
                item.descripcion ||
                String(item.label || item || 'Unknown'),
            value:
                item.code ||
                item.codigo ||
                item.id ||
                String(item.value || item || 'unknown')
        }))
}

export const HousingControl = ({
    control,
    watchCountryResidence,
    watchDepartmentResidence
}: HousingControlProps): JSX.Element => {
    const { t } = useTranslation()
    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const [residenceList, setResidenceList] = useState<OptionsField[]>([])
    const [livingType, setLivingType] = useState<OptionsField[]>([])
    const [socioeconomicStatrum, setSocioeconomicStatrum] = useState<
        OptionsField[]
    >([])

    useEffect(() => {
        const fetchResources = async (): Promise<void> => {
            try {
                const residenceListResponse =
                    await resourcesApi.getResidenceZone()
                const residenceData =
                    residenceListResponse?.data || residenceListResponse || []

                setResidenceList(mapApiDataToOptions(residenceData))

                const livingTypeResponse = await resourcesApi.getLivingType()
                const livingTypeData =
                    livingTypeResponse?.data || livingTypeResponse || []

                setLivingType(mapApiDataToOptions(livingTypeData))

                const socioeconomicResponse =
                    await resourcesApi.getSocioeconomicStatus()
                const socioeconomicData =
                    socioeconomicResponse?.data || socioeconomicResponse || []

                setSocioeconomicStatrum(mapApiDataToOptions(socioeconomicData))
            } catch (error) {
                console.error('Failed to fetch resources:', error)
            }
        }

        fetchResources()
    }, [])

    return (
        <ContainerGroupField tittleGroup={t('forms.groups.housing')}>
            <AutocompleteControl
                name="zoneResidence"
                control={control}
                label={t('demographicInformation.zoneResidence')}
                options={residenceList}
                rules={{
                    required: t(
                        'demographicInformation.required.zoneResidence'
                    )
                }}
                isDisabled={!watchCountryResidence}
                disabledTooltip="demographicInformation.fillResidenceFirst"
            />
            {/* Solo mostrar tipo de vivienda y estrato social si el país de residencia es Colombia */}
            {(watchCountryResidence === 'CO' ||
                watchCountryResidence === 'Colombia') && (
                    <>
                        <AutocompleteControl
                            name="typeHouse"
                            control={control}
                            label={t('demographicInformation.typeHouse')}
                            options={livingType}
                            rules={{
                                required: t(
                                    'demographicInformation.required.typeHouse'
                                )
                            }}
                        />
                        <AutocompleteControl
                            name="socialStatus"
                            control={control}
                            label={t('demographicInformation.socialStatus')}
                            options={socioeconomicStatrum}
                            rules={{
                                required: t(
                                    'demographicInformation.required.socialStatus'
                                )
                            }}
                        />
                    </>
                )}
            <ConditionalAddressControl
                name="address"
                control={control}
                label={t('demographicInformation.address')}
                placeholder="address.enterAddress"
                isRequired
                rules={{
                    required: t(
                        'demographicInformation.required.address'
                    )
                }}
                countryResidence={watchCountryResidence}
                isDisabled={!watchCountryResidence}
                disabledTooltip="demographicInformation.fillResidenceFirst"
                department={watchDepartmentResidence}
            />
        </ContainerGroupField>
    )
}
